import type { CSSProperties } from 'react';

import { hueOf, initialOf } from '@/shared/lib/avatar';

import styles from './Avatar.module.css';

interface AvatarProps {
  /** Stable identity (user id): drives the color. */
  id: string;
  name: string;
  size?: 'sm' | 'md';
  /** Online / presence marker. */
  online?: boolean;
  className?: string;
}

/** A circular initial. The name is the tooltip and the accessible label. */
export function Avatar({ id, name, size = 'md', online, className }: AvatarProps) {
  return (
    <span
      className={[styles.avatar, styles[size], className].filter(Boolean).join(' ')}
      style={{ '--avatar-hue': hueOf(id) } as CSSProperties}
      title={name}
      role="img"
      aria-label={name}
      data-online={online ? 'true' : undefined}
    >
      <span aria-hidden>{initialOf(name)}</span>
    </span>
  );
}

export interface AvatarPerson {
  id: string;
  name: string;
  online?: boolean;
}

interface StackProps {
  people: readonly AvatarPerson[];
  /** At most this many circles are drawn; the rest become one "+N" circle. Default 3. */
  max?: number;
  size?: 'sm' | 'md';
  /** Accessible name of the whole group. */
  label?: string;
}

/** Overlapping avatars with a `+N` overflow circle. Renders nothing for an empty list. */
export function AvatarStack({ people, max = 3, size = 'md', label }: StackProps) {
  if (people.length === 0) return null;
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  return (
    <span
      className={styles.stack}
      role="group"
      {...(label !== undefined && { 'aria-label': label })}
    >
      {shown.map((person) => (
        <Avatar
          key={person.id}
          id={person.id}
          name={person.name}
          size={size}
          {...(person.online !== undefined && { online: person.online })}
        />
      ))}
      {rest > 0 && (
        <span
          className={`${styles.avatar} ${styles[size]} ${styles.more}`}
          title={people
            .slice(max)
            .map((p) => p.name)
            .join(', ')}
        >
          +{rest}
        </span>
      )}
    </span>
  );
}
